import { useEffect, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_VERSION = 'v3';

// Recursively split a tree into { strings: {path: value}, template: tree-with-null-strings }
function extractStrings(node, path = '', acc = { strings: {}, structure: null }) {
  if (node === null || node === undefined) return { strings: acc.strings, structure: node };
  if (typeof node === 'string') {
    acc.strings[path] = node;
    return { strings: acc.strings, structure: `__T__${path}` };
  }
  if (typeof node !== 'object') return { strings: acc.strings, structure: node };
  if (Array.isArray(node)) {
    const outArr = node.map((v, i) => extractStrings(v, `${path}[${i}]`, acc).structure);
    return { strings: acc.strings, structure: outArr };
  }
  // Skip React components / functions
  const out = {};
  for (const k of Object.keys(node)) {
    const v = node[k];
    if (typeof v === 'function' || (v && v.$$typeof)) { out[k] = v; continue; }
    out[k] = extractStrings(v, path ? `${path}.${k}` : k, acc).structure;
  }
  return { strings: acc.strings, structure: out };
}

// Rehydrate: walk the template and swap __T__path markers with translated strings
function rehydrate(template, translations, originalNode) {
  if (typeof template === 'string' && template.startsWith('__T__')) {
    const key = template.slice(5);
    return translations[key] ?? originalNode;
  }
  if (Array.isArray(template)) {
    return template.map((v, i) => rehydrate(v, translations, originalNode ? originalNode[i] : undefined));
  }
  if (template && typeof template === 'object') {
    const out = {};
    for (const k of Object.keys(template)) {
      const orig = originalNode ? originalNode[k] : undefined;
      // Preserve React components / functions passed through as-is
      if (typeof template[k] === 'function' || (template[k] && template[k].$$typeof)) { out[k] = template[k]; continue; }
      out[k] = rehydrate(template[k], translations, orig);
    }
    return out;
  }
  return template;
}

/**
 * useTranslatedContent — returns page content translated to current UI language.
 * Preserves non-string values (icons/components) intact by only translating string leaves.
 */
export function useTranslatedContent(CONTENT, cacheKey) {
  const { language } = useLanguage();
  const isBuiltIn = language === 'fr' || language === 'en';
  const base = CONTENT.fr || CONTENT.en;

  const [translated, setTranslated] = useState(
    isBuiltIn ? (CONTENT[language] || CONTENT.en) : (CONTENT.en || base)
  );

  useEffect(() => {
    if (isBuiltIn) {
      setTranslated(CONTENT[language] || CONTENT.en);
      return;
    }

    const key = `plexterra:i18n:${CACHE_VERSION}:${language}:${cacheKey}`;
    const cached = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
    // Always recompute structure from `base` so React components (icons) are preserved.
    // localStorage cannot serialize forward_ref components (they become {}), so we only
    // cache the flat translations dict and rebuild the template each mount.
    const { strings, structure } = extractStrings(base);

    if (cached) {
      try {
        const cachedTranslations = JSON.parse(cached);
        setTranslated(rehydrate(structure, cachedTranslations, base));
        return;
      } catch { /* fall through to fetch */ }
    }

    let cancelled = false;
    (async () => {
      const attemptFetch = async () => {
        const resp = await fetch(`${API}/api/translate/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payload: strings, source: CONTENT.fr ? 'fr' : 'en', target: language }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.json();
      };
      // Retry with backoff for large payloads / upstream 502s (Claude cold-start)
      const backoffs = [0, 2000, 6000];
      let lastErr = null;
      for (let i = 0; i < backoffs.length; i++) {
        if (cancelled) return;
        if (backoffs[i] > 0) await new Promise(r => setTimeout(r, backoffs[i]));
        try {
          const data = await attemptFetch();
          if (cancelled) return;
          const merged = rehydrate(structure, data.translations, base);
          setTranslated(merged);
          try { window.localStorage.setItem(key, JSON.stringify(data.translations)); } catch {/* quota */}
          return;
        } catch (err) {
          lastErr = err;
        }
      }
      if (!cancelled) {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] translation fetch failed after retries for ${language}/${cacheKey}:`, lastErr?.message);
        setTranslated(CONTENT.en || base);
      }
    })();

    return () => { cancelled = true; };
  }, [language, cacheKey, isBuiltIn]); // eslint-disable-line react-hooks/exhaustive-deps

  return { c: translated, language };
}

export default useTranslatedContent;

